import { Box, Drawer } from '@mantine/core';
import { Text } from '@repo/foundations';
import { orderBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import type { IDatabuilderJob, IIntegration } from '../../../api';
import { useIntegrationSpec } from '../../../api';
import { DatabuilderJobType } from '../../../interfaces';
import { sentenceCase } from '../../../utils/stringUtils';
import { hashCode } from '../../../utils/utils';
import { ErrorLogs } from '../../ErrorLogs/ErrorLogs';
import { IntegrationHelp } from '../IntegrationHelp';
import { ExtractionStagesTable } from './ExtractionStagesTable';
import { ExtractionSummary } from './ExtractionSummary';
import { SuggestionBox } from './SuggestionBox';

interface ExtractionDetailProps {
	integration: IIntegration;
	job: IDatabuilderJob;
	onClose: () => void;
}

const MAX_FAILED_STAGES_WITH_LOGS = 10;

export const ExtractionDetail = observer(
	({ integration, job, onClose }: ExtractionDetailProps) => {
		const spec = useIntegrationSpec(integration.id);

		const failedStagesWithLogs = useMemo(
			() =>
				job.stages?.filter(
					(stage) => stage.stage && stage.failed && stage.logs
				),
			[job.stages]
		);

		return (
			<Drawer
				opened
				onClose={onClose}
				position="right"
				size="lg"
				title={
					<Text size="md" weight="bold">
						Sync stages
					</Text>
				}
			>
				<Box p={4}>
					<ExtractionStagesTable
						// Only include stages that include a `stage` title.
						stages={orderBy(
							(job.stages ?? []).map((stage) =>
								stage.stage
									? { ...stage, stage: stage.stage }
									: { ...stage, stage: '[Untitled Stage]' }
							),
							(s) => s.created_at
						).map((stage) => ({
							...stage,
							stage: sentenceCase(stage.stage),
						}))}
						isJobTerminated={job.terminated}
					/>
					<Box mb={4} />
					{failedStagesWithLogs.length > 0 && (
						<Box key={hashCode(failedStagesWithLogs[0].id)}>
							{failedStagesWithLogs
								.slice(0, MAX_FAILED_STAGES_WITH_LOGS)
								.map((stage) => (
									<ErrorLogs
										key={hashCode(stage.id)}
										title={
											stage.stage +
											': ' +
											(stage.logs.split('\n')[0] || 'Runtime Error')
										}
										logs={stage.logs.split('\n').slice(1)}
										initiallyOpened={false}
									/>
								))}
							{failedStagesWithLogs.length > MAX_FAILED_STAGES_WITH_LOGS && (
								<Text size="sm" color="text/secondary/default">
									+ {failedStagesWithLogs.length - MAX_FAILED_STAGES_WITH_LOGS}{' '}
									more...
								</Text>
							)}
							{job.suggestion && (
								<SuggestionBox
									title={job.suggestion.title}
									message={job.suggestion.text}
								/>
							)}
							{spec && <IntegrationHelp spec={spec} />}
						</Box>
					)}
					{!job.failed &&
						// Only show summary if it's a pull, dummy, or is Glean
						(job.type === DatabuilderJobType.METADATA ||
							job.type === DatabuilderJobType.DUMMY ||
							integration.type === 'glean') && <ExtractionSummary job={job} />}
				</Box>
			</Drawer>
		);
	}
);
